import type {
  ListingIdentitySection,
  ListingCatalogSection,
  ListingSeoSection,
} from "../../../../types/listing-wizard.types";

export type PublishDistribution = ListingSeoSection['visibilityMode'];

/** Wizard-only: legacy `draft` and unknown values map to a publish intent. */
export function normalizePublishDistribution(
  mode: string | undefined | null,
): PublishDistribution {
  return mode === 'unlisted' ? 'unlisted' : 'public';
}

export function publishDistributionLabel(mode: PublishDistribution): string {
  return mode === 'unlisted'
    ? 'Unlisted — shareable link, hidden from shop search'
    : 'Public — visible in shop, search, and collections';
}
import {
  buildListingDiscoveryTagCatalog,
  toTagSlug,
} from "../constants/listingTaxonomy";

interface Product {
  id: string;
  name: string;
}

/**
 * Build SEO / shop tags from structured identity + catalog (no random title tokenization).
 */
export const generateSeoSuggestedTags = (
  identityData: ListingIdentitySection,
  catalogData: ListingCatalogSection,
  availableProducts: Product[],
  /** When set (e.g. from API snapshot), must align with server allow-list. */
  fullDiscoveryCatalog?: string[]
): string[] => {
  const out: string[] = [];

  if (identityData.primaryMarketNicheKey) {
    out.push(identityData.primaryMarketNicheKey);
  }
  if (identityData.marketSubNicheKey) {
    out.push(identityData.marketSubNicheKey);
  }

  for (const c of identityData.productCategoryKeys ?? []) {
    const s = String(c).trim();
    if (s) out.push(s);
  }
  for (const pt of identityData.productTypeKeys ?? []) {
    const s = String(pt).trim();
    if (s) out.push(s);
  }

  const styleKeyList = identityData.styleKeys ?? [];
  for (const k of styleKeyList) {
    const s = toTagSlug(String(k));
    if (s) out.push(s);
  }
  for (const t of identityData.styleTags) {
    const s = toTagSlug(t);
    if (s) out.push(s);
  }
  const audKeys = identityData.audienceKeys ?? [];
  for (const k of audKeys) {
    const s = toTagSlug(String(k));
    if (s) out.push(s);
  }
  for (const a of identityData.audience) {
    const s = toTagSlug(a);
    if (s) out.push(s);
  }
  const occKeys = identityData.occasionKeys ?? [];
  for (const k of occKeys) {
    const s = toTagSlug(String(k));
    if (s) out.push(s);
  }
  for (const o of identityData.occasions) {
    const s = toTagSlug(o);
    if (s) out.push(s);
  }

  if (catalogData.selectedProductIds.length > 0) {
    const productNames = availableProducts
      .filter((p) => catalogData.selectedProductIds.includes(p.id))
      .map((p) => {
        const first = p.name.trim().split(/\s+/)[0] ?? "";
        return toTagSlug(first);
      })
      .filter(Boolean);
    out.push(...productNames.slice(0, 2));
  }

  out.push("morocco");

  const fullCatalog =
    fullDiscoveryCatalog && fullDiscoveryCatalog.length > 0
      ? fullDiscoveryCatalog
      : buildListingDiscoveryTagCatalog();
  const merged = Array.from(new Set([...out, ...fullCatalog])).filter(
    Boolean
  );
  merged.sort((a, b) => a.localeCompare(b));
  return merged.slice(0, 80);
};

/** Ordered chips for Step 6: suggestions first, then remaining catalog. */
export function mergeSeoTagOptionsForUi(
  suggestedOrdered: string[],
  catalog: string[]
): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const t of suggestedOrdered) {
    if (!seen.has(t)) {
      seen.add(t);
      ordered.push(t);
    }
  }
  for (const t of catalog) {
    if (!seen.has(t)) {
      seen.add(t);
      ordered.push(t);
    }
  }
  return ordered;
}
